
import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import { Code, Monitor, Settings, Zap, Users, Target, Globe, BarChart3, ExternalLink, Briefcase, Clock, CheckCircle } from 'lucide-react';

const ExpertisePage = () => {
  const technicalSkills = [
    "Adobe Creative Suite", "Canva Pro", "WordPress", "Drupal", "HubSpot",
    "Google Analytics", "Google Ads", "Meta Business", "LinkedIn Ads",
    "Hootsuite", "Buffer", "Mailchimp", "Sendinblue", "Figma",
    "Sketch", "InVision", "Slack", "Asana", "Trello", "Monday.com"
  ];

  const coreCompetencies = [
    {
      icon: Target,
      title: "Stratégie de Communication Globale",
      description: "Conception et déploiement de plans de communication intégrés alignés sur vos objectifs business.",
      skills: ["Audit de communication", "Positionnement de marque", "Stratégie éditoriale", "Plan média", "Budget et ROI"],
      link: "/competences/strategie-communication"
    },
    {
      icon: Globe,
      title: "Communication Digitale & Médias Sociaux",
      description: "Maîtrise complète de l'écosystème digital pour maximiser votre présence en ligne.",
      skills: ["Social Media Management", "Content Marketing", "SEO/SEM", "Email Marketing", "Influence Marketing"],
      link: "/competences/communication-digitale"
    },
    {
      icon: Users,
      title: "Relations Publiques & Médias",
      description: "Développement et gestion de relations durables avec les médias et influenceurs.",
      skills: ["Relations presse", "Événementiel", "Gestion de crise", "Storytelling", "Personal branding"],
      link: "/competences/relations-publiques"
    },
    {
      icon: BarChart3,
      title: "Analyse & Optimisation",
      description: "Mesure de performance et optimisation continue de vos actions de communication.",
      skills: ["Analytics & KPI", "A/B Testing", "Reporting", "Data visualization", "Recommandations stratégiques"],
      link: "/competences/analyse-optimisation"
    },
    {
      icon: Monitor,
      title: "Solutions Digitales",
      description: "Conception et développement de plateformes digitales innovantes.",
      skills: ["Site web & e-commerce", "UX/UI Design", "Applications mobiles", "Automation", "Intégrations"],
      link: "/competences/solutions-digitales"
    },
    {
      icon: Zap,
      title: "Innovation & Transformation",
      description: "Accompagnement dans l'adoption de nouvelles technologies et méthodologies.",
      skills: ["Transformation digitale", "Change management", "Formation équipes", "Veille technologique", "Prospective"],
      link: "/competences/innovation-transformation"
    }
  ];

  const diplomes = [
    {
      title: "Bachelor Communication Digitale",
      institution: "École Supérieure de Commerce",
      year: "2016"
    },
    {
      title: "Master Marketing Digital",
      institution: "Université Paris Dauphine",
      year: "2018"
    },
    {
      title: "Certification Community Management",
      institution: "OpenClassrooms",
      year: "2019"
    },
    {
      title: "Google Analytics Certified",
      institution: "Google",
      year: "2024"
    },
    {
      title: "Facebook Blueprint Certification",
      institution: "Meta",
      year: "2023"
    },
    {
      title: "LinkedIn Marketing Solutions",
      institution: "LinkedIn",
      year: "2024"
    }
  ];

  const freelanceProcess = [
    {
      step: "1",
      title: "Analyse & Audit",
      description: "Étude approfondie de votre marque, concurrence et objectifs pour définir une stratégie sur-mesure."
    },
    {
      step: "2",
      title: "Stratégie Personnalisée",
      description: "Création d'un plan d'action détaillé avec KPI et timeline précis pour atteindre vos objectifs."
    },
    {
      step: "3",
      title: "Mise en Œuvre",
      description: "Déploiement des actions définies avec suivi régulier et ajustements selon les performances."
    },
    {
      step: "4",
      title: "Optimisation Continue",
      description: "Analyse des résultats et optimisation constante pour maximiser votre ROI."
    }
  ];

  const taches = [
    {
      category: "Stratégie",
      tasks: [
        "Audit de communication globale",
        "Définition du positionnement de marque",
        "Élaboration de la stratégie éditoriale",
        "Planification média et budgets"
      ]
    },
    {
      category: "Création",
      tasks: [
        "Création de contenus visuels et rédactionnels",
        "Design d'identité visuelle",
        "Production de campagnes publicitaires",
        "Développement de supports de communication"
      ]
    },
    {
      category: "Gestion",
      tasks: [
        "Animation des réseaux sociaux",
        "Gestion des relations presse",
        "Coordination d'événements",
        "Suivi des campagnes publicitaires"
      ]
    },
    {
      category: "Analyse",
      tasks: [
        "Reporting de performance",
        "Analyse des KPI",
        "Veille concurrentielle",
        "Recommandations d'optimisation"
      ]
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-navy-50 via-white to-blue-50">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-sans font-light text-navy-900 mb-8">
              Mon <span className="font-semibold text-blue-600">Expertise</span> au service 
              <br />de votre <span className="font-semibold bg-gradient-to-r from-blue-600 to-gold-500 bg-clip-text text-transparent">succès</span>
            </h1>
            <p className="text-xl text-navy-600 font-light leading-relaxed max-w-3xl mx-auto">
              Découvrez l'ensemble de mes compétences techniques et stratégiques, 
              forgées par 8 années d'expérience dans l'accompagnement de marques ambitieuses.
            </p>
          </div>
        </div>
      </section>

      {/* Core Competencies */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-sans font-light text-navy-900 mb-6">
              Compétences <span className="font-semibold text-blue-600">Clés</span>
            </h2>
            <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
              Une expertise complète pour répondre à tous vos besoins en communication et branding.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {coreCompetencies.map((competency, index) => (
              <Link 
                key={index} 
                to={competency.link}
                className="bg-white border border-navy-100 rounded-2xl p-8 shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-1 group"
              >
                <div className="bg-gradient-to-r from-blue-600 to-navy-700 p-4 rounded-xl w-16 h-16 mb-6">
                  <competency.icon className="text-white" size={32} />
                </div>
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-xl font-sans font-semibold text-navy-900">{competency.title}</h3>
                  <ExternalLink className="text-blue-600 opacity-0 group-hover:opacity-100 transition-opacity" size={20} />
                </div>
                <p className="text-navy-600 font-light mb-6 leading-relaxed">{competency.description}</p>
                <ul className="space-y-2">
                  {competency.skills.map((skill, skillIndex) => (
                    <li key={skillIndex} className="flex items-center gap-2 text-sm text-navy-600">
                      <div className="w-1.5 h-1.5 bg-blue-500 rounded-full"></div>
                      {skill}
                    </li>
                  ))}
                </ul>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Technical Skills */}
      <section className="py-20 bg-gradient-to-br from-blue-50/30 to-navy-50/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-navy-700 text-white px-6 py-3 rounded-full text-sm font-medium mb-6">
              <Code size={18} />
              Outils & Technologies
            </div>
            <h2 className="text-4xl font-sans font-light text-navy-900 mb-6">
              Maîtrise <span className="font-semibold text-blue-600">Technique</span>
            </h2>
            <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
              Une palette d'outils modernes pour des solutions digitales performantes et innovantes.
            </p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="flex flex-wrap gap-3 justify-center">
              {technicalSkills.map((skill, index) => (
                <Link
                  key={index}
                  to={`/outils/${skill.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')}`}
                  className="px-6 py-3 bg-white border border-navy-100 rounded-full text-navy-700 font-medium hover:bg-blue-50 hover:border-blue-200 transition-colors duration-200 shadow-sm group"
                >
                  <span className="flex items-center gap-2">
                    {skill}
                    <ExternalLink className="opacity-0 group-hover:opacity-100 transition-opacity" size={14} />
                  </span>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Comment fonctionne un Freelance */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-sans font-light text-navy-900 mb-6">
              Comment fonctionne un <span className="font-semibold text-blue-600">Freelance</span>
            </h2>
            <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
              Une approche structurée et transparente pour des résultats optimaux.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {freelanceProcess.map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-r from-blue-600 to-navy-700 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-6">
                  {item.step}
                </div>
                <h3 className="text-xl font-semibold text-navy-900 mb-4">{item.title}</h3>
                <p className="text-navy-600 font-light leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mes Tâches */}
      <section className="py-20 bg-gradient-to-br from-blue-50/30 to-navy-50/30">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <div className="inline-flex items-center gap-2 bg-gradient-to-r from-blue-600 to-navy-700 text-white px-6 py-3 rounded-full text-sm font-medium mb-6">
              <Briefcase size={18} />
              Missions & Tâches
            </div>
            <h2 className="text-4xl font-sans font-light text-navy-900 mb-6">
              Mes <span className="font-semibold text-blue-600">Tâches</span> Quotidiennes
            </h2>
            <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
              Un aperçu concret de mes activités pour votre projet.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
            {taches.map((category, index) => (
              <div key={index} className="bg-white rounded-2xl p-6 shadow-lg">
                <h3 className="text-xl font-semibold text-navy-900 mb-4 text-center">{category.category}</h3>
                <ul className="space-y-3">
                  {category.tasks.map((task, taskIndex) => (
                    <li key={taskIndex} className="flex items-start gap-2 text-navy-600">
                      <CheckCircle className="text-blue-600 mt-0.5 flex-shrink-0" size={16} />
                      <span className="text-sm leading-relaxed">{task}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Diplômes */}
      <section className="py-20">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-sans font-light text-navy-900 mb-6">
              Formation & <span className="font-semibold text-blue-600">Diplômes</span>
            </h2>
            <p className="text-lg text-navy-600 font-light max-w-2xl mx-auto">
              Un parcours académique et professionnel rigoureux.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {diplomes.map((diplome, index) => (
              <div key={index} className="bg-white border border-navy-100 rounded-xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300">
                <div className="w-12 h-12 bg-gradient-to-r from-blue-600 to-navy-700 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Settings className="text-white" size={24} />
                </div>
                <h3 className="font-semibold text-navy-900 mb-2">{diplome.title}</h3>
                <p className="text-blue-600 font-medium mb-1">{diplome.institution}</p>
                <p className="text-navy-500 text-sm">{diplome.year}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppButton />
    </div>
  );
};

export default ExpertisePage;
